import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardContent, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Save, Eye, Calendar, Image as ImageIcon,
  Upload, X, Tag, Plus
} from 'lucide-react';

const BlogEditor = () => {
  const navigate = useNavigate();
  const { postId } = useParams();
  const isEditing = !!postId;
  const editorRef = useRef(null);

  // Helper to construct full image URL
  const getImageUrl = (url) => {
    if (!url) return '';
    
    // Handle if url is an object (thumbnail object)
    if (typeof url === 'object' && url.url) {
      url = url.url;
    }
    
    if (url.startsWith('http')) return url; // Already a full URL
    
    // Images are in frontend public folder, use relative path
    return url;
  };

  const [loading, setLoading] = useState(isEditing); // Only show loading spinner when editing
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [addingCategory, setAddingCategory] = useState(false);
  const [post, setPost] = useState({
    title: '',
    slug: '',
    seo_title: '',
    meta_description: '',
    excerpt: '',
    content: '',
    thumbnail: null,
    status: 'draft',
    categories: [],
    primary_category: null,  // Primary category for URL structure
    tags: [],
    faqs: [],  // FAQ array for rich snippets
    featured: false,
    allow_comments: true,
    scheduled_for: null
  });

  const [uploadingThumbnail, setUploadingThumbnail] = useState(false);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    console.log('BlogEditor mounted', { postId, isEditing });
    setLoading(false); // Immediately stop loading for testing
    fetchCategories();
    if (isEditing) {
      fetchPost();
    }
  }, [postId]);

  const fetchCategories = async () => {
    try {
      console.log('Fetching categories...');
      const response = await api.get('/blog/categories');
      console.log('Categories fetched:', response.data);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      setCategories([]);
    }
  };

  const fetchPost = async () => {
    try {
      console.log('Fetching post:', postId);
      const response = await api.get(`/admin/blog/posts/${postId}`);
      const foundPost = response.data;
      
      if (foundPost) {
        console.log('Post found:', foundPost);
        setPost(foundPost);
      } else {
        console.log('Post not found');
        alert('Blog post not found');
      }
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch post:', error);
      alert('Failed to load blog post: ' + (error.response?.data?.detail || error.message));
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setPost(prev => ({ ...prev, [field]: value }));
    
    // Auto-generate slug from title
    if (field === 'title' && !isEditing) {
      const slug = value
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
      setPost(prev => ({ ...prev, slug }));
    }
  };

  const handleThumbnailUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingThumbnail(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt_text', post.title || 'Blog post thumbnail');
      formData.append('title', post.title || '');

      const response = await api.post('/admin/blog/upload-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPost(prev => ({
        ...prev,
        thumbnail: {
          url: response.data.url,
          alt_text: response.data.alt_text,
          title: response.data.title
        }
      }));
    } catch (error) {
      console.error('Failed to upload thumbnail:', error);
      alert('Failed to upload image');
    } finally {
      setUploadingThumbnail(false);
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !post.tags.includes(tagInput.trim())) {
      setPost(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setPost(prev => ({
      ...prev,
      tags: prev.tags.filter(t => t !== tag)
    }));
  };

  const handleCreateCategory = async () => {
    if (!newCategoryName.trim()) {
      alert('Please enter a category name');
      return;
    }

    setAddingCategory(true);
    try {
      const response = await api.post('/admin/blog/categories', {
        name: newCategoryName.trim(),
        slug: newCategoryName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
      });
      
      const newCategory = response.data;
      setCategories(prev => [...prev, newCategory]);
      setPost(prev => ({
        ...prev,
        categories: [...prev.categories, newCategory.id]
      }));
      setNewCategoryName('');
      alert('Category created successfully!');
    } catch (error) {
      console.error('Failed to create category:', error);
      alert(error.response?.data?.detail || 'Failed to create category');
    } finally {
      setAddingCategory(false);
    }
  };

  const handleAddFAQ = () => {
    setPost(prev => ({
      ...prev,
      faqs: [...prev.faqs, { question: '', answer: '' }]
    }));
  };

  const handleRemoveFAQ = (index) => {
    setPost(prev => ({
      ...prev,
      faqs: prev.faqs.filter((_, i) => i !== index)
    }));
  };

  const handleFAQChange = (index, field, value) => {
    setPost(prev => ({
      ...prev,
      faqs: prev.faqs.map((faq, i) => 
        i === index ? { ...faq, [field]: value } : faq
      )
    }));
  };

  const handleSave = async (status) => {
    if (!post.title.trim()) {
      alert('Please enter a title');
      return;
    }

    setSaving(true);
    try {
      const data = { ...post, status };

      if (isEditing) {
        await api.put(`/admin/blog/posts/${postId}`, data);
        alert('Post updated successfully!');
      } else {
        await api.post('/admin/blog/posts', data);
        alert('Post created successfully!');
      }

      navigate('/admin/blog');
    } catch (error) {
      console.error('Failed to save post:', error);
      alert(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#EB2F5B] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin/blog')}
              variant="outline"
              size="sm"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Posts
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                {isEditing ? 'Edit Post' : 'New Post'}
              </h1>
            </div>
          </div>

          <div className="flex gap-2">
            <Button
              onClick={() => handleSave('draft')}
              variant="outline"
              disabled={saving}
            >
              <Save className="h-4 w-4 mr-2" />
              Save Draft
            </Button>
            <Button
              onClick={() => handleSave('published')}
              className="bg-[#EB2F5B] hover:bg-[#d02850]"
              disabled={saving}
            >
              {saving ? 'Publishing...' : 'Publish'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="col-span-2 space-y-6">
            {/* Title */}
            <Card>
              <CardContent className="p-6">
                <Label>Title *</Label>
                <Input
                  type="text"
                  value={post.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Enter post title..."
                  className="text-2xl font-bold border-0 p-0 focus:ring-0"
                />
              </CardContent>
            </Card>

            {/* Content Editor */}
            <Card>
              <CardHeader>
                <CardTitle>Content</CardTitle>
              </CardHeader>
              <CardContent>
                <Editor
                  apiKey="4blzvcxlh6p1odeflxu2ctwx4kweidlk24y1xi6mgklf4mvl"
                  onInit={(evt, editor) => editorRef.current = editor}
                  value={post.content}
                  onEditorChange={(content) => handleInputChange('content', content)}
                  init={{
                    height: 500,
                    menubar: false,
                    plugins: [
                      'advlist', 'autolink', 'lists', 'link', 'image', 'charmap',
                      'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                      'insertdatetime', 'media', 'table', 'preview', 'help', 'wordcount'
                    ],
                    toolbar: 'undo redo | blocks | ' +
                      'bold italic forecolor | alignleft aligncenter ' +
                      'alignright alignjustify | bullist numlist outdent indent | ' +
                      'removeformat | help',
                    content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                    block_formats: 'Paragraph=p; Heading 2=h2; Heading 3=h3; Heading 4=h4',
                  }}
                />
              </CardContent>
            </Card>

            {/* Excerpt */}
            <Card>
              <CardHeader>
                <CardTitle>Excerpt</CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={post.excerpt || ''}
                  onChange={(e) => handleInputChange('excerpt', e.target.value)}
                  placeholder="Brief summary of the post (optional)"
                  rows={3}
                />
              </CardContent>
            </Card>

            {/* SEO Settings */}
            <Card>
              <CardHeader>
                <CardTitle>SEO Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>SEO Title</Label>
                  <Input
                    type="text"
                    value={post.seo_title || ''}
                    onChange={(e) => handleInputChange('seo_title', e.target.value)}
                    placeholder="Title for search engines"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {(post.seo_title || post.title).length}/60 characters
                  </p>
                </div>

                <div>
                  <Label>Meta Description</Label>
                  <Textarea
                    value={post.meta_description || ''}
                    onChange={(e) => handleInputChange('meta_description', e.target.value)}
                    placeholder="Description for search engines"
                    rows={3}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {(post.meta_description || '').length}/160 characters
                  </p>
                </div>

                <div>
                  <Label>URL Slug</Label>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-500">
                        /blog/{categories.find(c => c.id === post.primary_category)?.slug || '[category]'}/
                      </span>
                      <Input
                        type="text"
                        value={post.slug}
                        onChange={(e) => handleInputChange('slug', e.target.value)}
                        placeholder="url-friendly-slug"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Full URL: /blog/{categories.find(c => c.id === post.primary_category)?.slug || '[category]'}/{post.slug || 'post-slug'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Featured Image */}
            <Card>
              <CardHeader>
                <CardTitle>Featured Image</CardTitle>
              </CardHeader>
              <CardContent>
                {post.thumbnail?.url ? (
                  <div className="space-y-3">
                    <div className="relative">
                      <img
                        src={getImageUrl(post.thumbnail.url)}
                        alt={post.thumbnail.alt_text || 'Thumbnail'}
                        className="w-full h-48 object-cover rounded"
                        onError={(e) => {
                          console.error('Image failed to load:', {
                            src: e.target.src,
                            thumbnail: post.thumbnail
                          });
                        }}
                      />
                      <Button
                        onClick={() => setPost(prev => ({ ...prev, thumbnail: null }))}
                        variant="destructive"
                        size="sm"
                        className="absolute top-2 right-2"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    {/* Alt Text Field */}
                    <div>
                      <Label className="text-xs">Alt Text (SEO)</Label>
                      <Input
                        type="text"
                        value={post.thumbnail.alt_text || ''}
                        onChange={(e) => setPost(prev => ({
                          ...prev,
                          thumbnail: { ...prev.thumbnail, alt_text: e.target.value }
                        }))}
                        placeholder="Describe the image for accessibility and SEO..."
                        className="text-sm"
                      />
                    </div>
                    {/* Image Title Field */}
                    <div>
                      <Label className="text-xs">Image Title (optional)</Label>
                      <Input
                        type="text"
                        value={post.thumbnail.title || ''}
                        onChange={(e) => setPost(prev => ({
                          ...prev,
                          thumbnail: { ...prev.thumbnail, title: e.target.value }
                        }))}
                        placeholder="Image title..."
                        className="text-sm"
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <label className="cursor-pointer">
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-[#EB2F5B] transition-colors">
                        <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm text-gray-600">
                          {uploadingThumbnail ? 'Uploading...' : 'Click to upload'}
                        </p>
                      </div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleThumbnailUpload}
                        className="hidden"
                        disabled={uploadingThumbnail}
                      />
                    </label>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Categories */}
            <Card>
              <CardHeader>
                <CardTitle>Categories</CardTitle>
              </CardHeader>
              <CardContent>
                {/* Primary Category Selector */}
                <div className="mb-4 pb-4 border-b">
                  <Label className="text-xs font-semibold text-gray-700 mb-2 block">
                    Primary Category (for URL structure)
                  </Label>
                  <select
                    value={post.primary_category || ''}
                    onChange={(e) => {
                      const categoryId = e.target.value;
                      setPost(prev => ({
                        ...prev,
                        primary_category: categoryId || null,
                        // Auto-add to categories if not already included
                        categories: categoryId && !prev.categories.includes(categoryId) 
                          ? [...prev.categories, categoryId]
                          : prev.categories
                      }));
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#EB2F5B]"
                  >
                    <option value="">Select primary category...</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    URL will be: /blog/{categories.find(c => c.id === post.primary_category)?.slug || 'category'}/{post.slug || 'post-title'}
                  </p>
                </div>

                {/* Additional Categories */}
                <Label className="text-xs font-semibold text-gray-700 mb-2 block">
                  Additional Categories
                </Label>
                <div className="space-y-2 mb-4">
                  {categories.map((cat) => (
                    <label key={cat.id} className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={post.categories.includes(cat.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPost(prev => ({
                              ...prev,
                              categories: [...prev.categories, cat.id]
                            }));
                          } else {
                            setPost(prev => ({
                              ...prev,
                              categories: prev.categories.filter(id => id !== cat.id),
                              // Clear primary if it's being unchecked
                              primary_category: prev.primary_category === cat.id ? null : prev.primary_category
                            }));
                          }
                        }}
                        className="rounded"
                      />
                      <span className="text-sm">{cat.name}</span>
                    </label>
                  ))}
                </div>
                
                {/* Add new category */}
                <div className="pt-3 border-t">
                  <Label className="text-xs text-gray-600 mb-2">Add New Category</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleCreateCategory())}
                      placeholder="Category name..."
                      className="text-sm"
                    />
                    <Button 
                      onClick={handleCreateCategory} 
                      size="sm"
                      disabled={addingCategory}
                      className="bg-[#EB2F5B] hover:bg-[#d02850]"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tags */}
            <Card>
              <CardHeader>
                <CardTitle>Tags</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2 mb-3">
                  <Input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                    placeholder="Add tag..."
                  />
                  <Button onClick={handleAddTag} size="sm">
                    <Tag className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {post.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-sm flex items-center gap-1"
                    >
                      {tag}
                      <button onClick={() => handleRemoveTag(tag)}>
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Settings */}
            <Card>
              <CardHeader>
                <CardTitle>Settings</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={post.featured}
                    onChange={(e) => handleInputChange('featured', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Featured Post</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={post.allow_comments}
                    onChange={(e) => handleInputChange('allow_comments', e.target.checked)}
                    className="rounded"
                  />
                  <span className="text-sm">Allow Comments</span>
                </label>
              </CardContent>
            </Card>

            {/* FAQs for Rich Snippets */}
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>FAQs (Rich Snippets)</CardTitle>
                  <Button 
                    onClick={handleAddFAQ}
                    size="sm"
                    className="bg-[#EB2F5B] hover:bg-[#d02850]"
                  >
                    <Plus className="h-4 w-4 mr-1" />
                    Add FAQ
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-gray-500 mb-4">
                  Add FAQs to help Google index your content with rich snippets for better SEO.
                </p>
                {post.faqs.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-4">
                    No FAQs added yet. Click "Add FAQ" to get started.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {post.faqs.map((faq, index) => (
                      <div key={index} className="border rounded-lg p-3 space-y-2">
                        <div className="flex justify-between items-start">
                          <span className="text-xs font-semibold text-gray-600">FAQ #{index + 1}</span>
                          <Button
                            onClick={() => handleRemoveFAQ(index)}
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 text-red-500 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                        <div>
                          <Label className="text-xs">Question</Label>
                          <Input
                            type="text"
                            value={faq.question}
                            onChange={(e) => handleFAQChange(index, 'question', e.target.value)}
                            placeholder="Enter question..."
                            className="text-sm"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Answer</Label>
                          <Textarea
                            value={faq.answer}
                            onChange={(e) => handleFAQChange(index, 'answer', e.target.value)}
                            placeholder="Enter answer..."
                            rows={3}
                            className="text-sm"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogEditor;
